/**
 * Fiche tarifaire imprimable ("Exporter en PDF" = ouvrir cette page dans un
 * nouvel onglet puis déclencher l'impression navigateur -> Enregistrer en
 * PDF). On sort volontairement du rendu react-pdf : le style demandé
 * (formes SVG, dégradés, ombres, polices Google) n'a pas d'équivalent
 * fidèle dans le moteur de mise en page de react-pdf, alors qu'un vrai
 * moteur de rendu de navigateur le gère nativement à l'impression.
 *
 * Esthétique alignée sur la maquette de référence tarification-site-vitrine.html.
 */

import { ALAIS_LOGO_DATA_URI } from './alaisLogoDataUri';

function escapeHtml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

const CHEVRON_SVG = '<svg viewBox="0 0 12 12"><polyline points="2,1 9,6 2,11" fill="none" stroke="#f2711c" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';

function featureItem(feature) {
    return `<li><span class="dot"></span>${escapeHtml(feature)}</li>`;
}

function optionRow(option) {
    return `<li><span class="chev">${CHEVRON_SVG}</span><span class="opt-name">${escapeHtml(option.label)}</span><span class="dots"></span><span class="price">${escapeHtml(option.priceLabel)}</span></li>`;
}

/**
 * @param {{
 *   documentTitle: string,
 *   productName: string,
 *   priceCaption: string,
 *   priceAmount: string,
 *   originalPriceAmount?: string,
 *   sectionHeading: string,
 *   sectionSubtitle?: string,
 *   features: string[],
 *   options: { label: string, priceLabel: string }[],
 *   notes?: string[],
 * }} data
 */
export function buildProductSheetHtml(data) {
    const {
        documentTitle,
        productName,
        priceCaption,
        priceAmount,
        originalPriceAmount,
        sectionHeading,
        sectionSubtitle,
        features = [],
        options = [],
        notes = [],
    } = data;

    const year = new Date().getFullYear();

    return `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="UTF-8">
<title>${escapeHtml(documentTitle)}</title>
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Poppins:wght@600;700;800&family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet">
<style>
  :root{
    --ink:#0c0c0c;
    --orange:#f2711c;
    --orange-deep:#e8630f;
    --paper:#ffffff;
    --grey:#6b6b6b;
    --line:#eae7e2;
    --cream:#faf9f7;
  }

  *{margin:0;padding:0;box-sizing:border-box;}

  body{
    font-family:'Inter',sans-serif;
    color:var(--ink);
    background:#e9e6e0;
    display:flex;
    justify-content:center;
    padding:40px 16px;
  }

  .sheet{
    position:relative;
    width:210mm;
    min-height:297mm;
    background:var(--paper);
    box-shadow:0 30px 60px -20px rgba(0,0,0,.35);
    overflow:hidden;
    border-radius:6px;
    /* Column flex so .content (flex:1) absorbs any leftover space and the
       brand footer always sits flush with the bottom of the sheet instead
       of trailing right after the last option/feature. */
    display:flex;
    flex-direction:column;
  }

  /* When the pagination script (see end of body) detects the sheet doesn't
     fit one A4 page, it clones a second .sheet for the Options section right
     after this one — force it onto its own physical page. */
  .sheet + .sheet{
    break-before:page;
    page-break-before:always;
  }
  .sheet.page-2 .content{
    padding-top:48px;
  }

  @media print{
    @page{
      size:A4;
      margin:0;
    }
    html, body{
      background:#fff;
      padding:0;
      display:block;
      /* Without this, Chrome's print dialog drops every background-color
         (the black hero, the orange feature dots) unless the user manually
         ticks "Graphiques d'arrière-plan" — force it instead of relying on
         that checkbox. */
      -webkit-print-color-adjust:exact;
      print-color-adjust:exact;
    }
    .sheet{
      width:210mm;
      min-height:297mm;
      box-shadow:none;
      border-radius:0;
      margin:0;
    }
    .block, .feature-list li, .option-list li{ break-inside:avoid; page-break-inside:avoid; }
    .brand-footer{ break-inside:avoid; page-break-inside:avoid; }
  }

  /* ---------- HERO ---------- */
  .hero{
    position:relative;
    flex:none;
    background:var(--ink);
    color:#fff;
    padding:48px 56px 48px;
    overflow:hidden;
  }
  .hero::after{
    content:"";
    position:absolute;
    left:-10%;
    bottom:-140px;
    width:120%;
    height:180px;
    background:var(--paper);
    border-radius:50%;
  }
  .hero-row{
    position:relative;
    z-index:2;
    display:flex;
    flex-direction:column;
  }
  .price-tag{
    display:flex;
    align-items:baseline;
    gap:8px;
    align-self:flex-end;
    margin-top:16px;
  }
  .hero h1{
    font-family:'Poppins',sans-serif;
    font-weight:800;
    font-size:56px;
    line-height:1;
    letter-spacing:-.01em;
  }
  .hero h1 span{
    display:block;
    color:#b9b9b9;
    font-size:30px;
    font-weight:700;
    margin-top:10px;
    letter-spacing:0;
  }
  .price-tag .from{
    font-size:13px;
    color:#b9b9b9;
    text-transform:uppercase;
    letter-spacing:.1em;
    font-weight:600;
  }
  .price-tag .was{
    font-size:16px;
    font-weight:600;
    color:#8a8a8a;
    text-decoration:line-through;
  }
  .price-tag .amount{
    font-family:'Poppins',sans-serif;
    font-size:30px;
    font-weight:700;
    color:#fff;
  }

  /* ---------- BODY ---------- */
  .content{
    flex:1;
    padding:8px 56px 28px;
  }

  .block{
    margin-top:30px;
  }
  .block-head{
    border-left:3px solid var(--orange);
    padding-left:16px;
    margin-bottom:18px;
  }
  .block-head h2{
    font-family:'Poppins',sans-serif;
    font-weight:700;
    font-size:22px;
  }
  .block-head p{
    font-style:italic;
    color:var(--grey);
    font-size:14.5px;
    margin-top:4px;
  }

  .feature-list{
    list-style:none;
    display:flex;
    flex-direction:column;
    gap:10px;
    padding-left:16px;
  }
  .feature-list li{
    display:flex;
    align-items:flex-start;
    gap:14px;
    font-size:15.5px;
    line-height:1.4;
  }
  .dot{
    flex:none;
    width:8px;height:8px;
    border-radius:50%;
    background:var(--orange);
    margin-top:7px;
  }
  .chev{
    flex:none;
    width:20px;height:20px;
    margin-top:1px;
    display:flex;align-items:center;justify-content:center;
  }
  .chev svg{width:12px;height:12px;}

  .option-list{
    list-style:none;
    display:flex;
    flex-direction:column;
    gap:6px;
    padding-left:16px;
  }
  .option-list li{
    display:flex;
    align-items:baseline;
    gap:10px;
    font-size:15.5px;
    padding:6px 10px;
    border-radius:10px;
  }
  .option-list .opt-name{
    display:flex;
    align-items:baseline;
    gap:8px;
    white-space:nowrap;
  }
  .option-list .dots{
    flex:1;
    border-bottom:2px dotted #f0b98a;
    margin:0 6px;
    transform:translateY(-4px);
  }
  .option-list .price{
    font-weight:700;
    white-space:nowrap;
    color:var(--ink);
  }

  /* ---------- FOOTER NOTES ---------- */
  .footnotes{
    margin-top:14px;
    display:flex;
    justify-content:flex-end;
    flex-wrap:wrap;
    gap:10px;
  }
  .footnotes .pill{
    border:1.5px solid #f4c199;
    border-radius:999px;
    padding:8px 24px;
    font-size:13px;
    font-weight:600;
    color:var(--orange-deep);
    letter-spacing:.03em;
    white-space:nowrap;
  }

  /* ---------- FOOTER BRAND ---------- */
  .brand-footer{
    position:relative;
    flex:none;
    margin-top:28px;
    background:#fff;
    color:#fff;
    height:120px;
    overflow:hidden;
  }
  .brand-footer svg{
    position:absolute;
    inset:0;
    width:100%;
    height:100%;
    display:block;
  }
  .footer-inner{
    position:relative;
    z-index:2;
    height:100%;
    display:flex;
    align-items:center;
    gap:16px;
    padding:0 56px;
  }
  .logo-mark{
    position:relative;
    z-index:2;
    width:46px;
    height:auto;
    flex:none;
    display:block;
  }
  .brand-text{
    position:relative;
    z-index:2;
    font-size:13px;
    line-height:1.5;
  }
  .brand-text .name{font-weight:700;}
  .brand-text a{color:#fff;text-decoration:none;opacity:.9;}

  @media screen and (max-width:600px){
    .hero{padding:48px 28px 84px;}
    .hero h1{font-size:40px;}
    .hero h1 span{font-size:22px;}
    .content{padding:8px 28px 36px;}
    .brand-footer{height:130px;}
    .footer-inner{padding:0 28px;}
    .option-list li{flex-wrap:wrap;}
  }
</style>
</head>
<body>

<div class="sheet">

  <!-- HERO -->
  <header class="hero">
    <div class="hero-row">
      <h1>Tarification<span>${escapeHtml(productName)}</span></h1>
      <div class="price-tag">
        <span class="from">${escapeHtml(priceCaption)}</span>
        ${originalPriceAmount ? `<span class="was">${escapeHtml(originalPriceAmount)}</span>` : ''}
        <span class="amount">${escapeHtml(priceAmount)}</span>
      </div>
    </div>
  </header>

  <div class="content">

    ${features.length > 0 ? `
    <section class="block features-block">
      <div class="block-head">
        <h2>${escapeHtml(sectionHeading)}</h2>
        ${sectionSubtitle ? `<p>${escapeHtml(sectionSubtitle)}</p>` : ''}
      </div>
      <ul class="feature-list">
        ${features.map(featureItem).join('\n        ')}
      </ul>
    </section>` : ''}

    ${options.length > 0 ? `
    <section class="block options-block">
      <div class="block-head">
        <h2>Options</h2>
      </div>
      <ul class="option-list">
        ${options.map(optionRow).join('\n        ')}
      </ul>
    </section>` : ''}

    ${notes.length > 0 ? `
    <div class="footnotes">
      ${notes.map((note) => `<div class="pill">${escapeHtml(note)}</div>`).join('\n      ')}
    </div>` : ''}

  </div>

  <!-- BRAND FOOTER -->
  <footer class="brand-footer">
    <svg viewBox="0 0 1000 100" preserveAspectRatio="none">
      <path d="M0,10 C 250,24 380,32 500,30 C 640,28 850,15 1000,7 L1000,100 L0,100 Z" fill="#fc996d"/>
      <path d="M0,11 L1000,55 L1000,100 L0,100 Z" fill="#ff6825"/>
    </svg>
    <div class="footer-inner">
      <img class="logo-mark" src="${ALAIS_LOGO_DATA_URI}" alt="Alaïs-Studio">
      <div class="brand-text">
        <div class="name">Alaïs-Studio® — ${year}</div>
        <a href="https://alais-studio.fr">https://alais-studio.fr</a>
      </div>
    </div>
  </footer>

</div>

<script>
  /**
   * Prints always target a single A4 sheet, but a package with a long
   * feature/option list can overflow it. Rather than let the browser's
   * default pagination push the brand footer alone onto an almost-empty
   * second page, this measures the natural (unstretched) content height
   * once webfonts are settled and, only if it doesn't fit, moves the
   * Options block onto its own cloned .sheet (with its own footer) forced
   * onto page 2 — the "Ce qui est inclus" block and the Frais de dossier
   * note always stay together on page 1.
   */
  (function () {
    function paginate() {
      try {
        var sheet = document.querySelector('.sheet');
        var content = sheet && sheet.querySelector('.content');
        var featuresBlock = content && content.querySelector('.features-block');
        var optionsBlock = content && content.querySelector('.options-block');
        var footer = sheet && sheet.querySelector('.brand-footer');
        if (!sheet || !content || !featuresBlock || !optionsBlock || !footer) return;

        var lastChild = content.lastElementChild;
        var heroTop = sheet.getBoundingClientRect().top;
        var contentBottom = lastChild.getBoundingClientRect().bottom;
        var naturalHeight = (contentBottom - heroTop) + footer.offsetHeight;
        var pageHeightPx = 297 * 96 / 25.4;

        if (naturalHeight <= pageHeightPx - 4) return;

        var page2 = document.createElement('div');
        page2.className = 'sheet page-2';
        var page2Content = document.createElement('div');
        page2Content.className = 'content';
        page2Content.appendChild(optionsBlock);
        page2.appendChild(page2Content);
        page2.appendChild(footer.cloneNode(true));
        sheet.parentNode.insertBefore(page2, sheet.nextSibling);
      } catch (e) {
        // Layout couldn't be measured reliably — fall back to the single
        // natural-flow sheet rather than risk a half-broken page.
      }
    }

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(paginate, paginate);
    } else {
      paginate();
    }
  })();
</script>

</body>
</html>`;
}
